import { useEffect, useMemo, useState } from 'react';
import { fetchCampaigns, fetchCampaign, fetchTemplates, fetchVariables, sendTestEmail } from '../../api/relances.api';

function renderTemplate(template, variables) {
  if (!template) return '';
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
}

export default function CommunicationTest() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [relanceType, setRelanceType] = useState('PAYMENT_REMINDER');
  const [contentMode, setContentMode] = useState('TEMPLATE');
  const [templateId, setTemplateId] = useState('');
  const [customEmailSubject, setCustomEmailSubject] = useState('');
  const [customEmailBody, setCustomEmailBody] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [variablesJson, setVariablesJson] = useState('{}');
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    loadTemplates();
    loadVariables();
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const result = await fetchCampaigns({ status: 'DRAFT', limit: 100 });
      setCampaigns(result.campaigns || []);
    } catch (err) {
      console.error('Error loading campaigns:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await fetchTemplates({ isActive: true });
      setTemplates(data || []);
      if (!data || data.length === 0) {
        setContentMode('CUSTOM');
        setTemplateId('');
      } else if (!templateId) {
        setTemplateId(data[0].id);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadVariables = async () => {
    try {
      const data = await fetchVariables();
      setVariables(data || {});
    } catch (err) {
      console.error('Error loading variables:', err);
    }
  };

  const handleSelectCampaign = async (id) => {
    setSelectedCampaignId(id);
    setFeedback(null);
    if (!id) return;
    try {
      const campaign = await fetchCampaign(id);
      setRelanceType(campaign.relance_type || 'PAYMENT_REMINDER');
      setContentMode(campaign.content_mode || 'TEMPLATE');
      setTemplateId(campaign.template_id || '');
      setCustomEmailSubject(campaign.custom_email_subject || '');
      setCustomEmailBody(campaign.custom_email_body || '');
      if (!campaign.template_id && (campaign.content_mode || 'TEMPLATE') === 'TEMPLATE') {
        setContentMode('CUSTOM');
      }
    } catch (err) {
      console.error('Error loading campaign:', err);
    }
  };

  const useSampleVariables = () => {
    const vars = variables[relanceType] || [];
    const obj = {};
    vars.forEach(v => {
      obj[v.key] = v.example || '';
    });
    setVariablesJson(JSON.stringify(obj, null, 2));
  };

  const parsedVariables = useMemo(() => {
    try {
      return JSON.parse(variablesJson || '{}');
    } catch {
      return null;
    }
  }, [variablesJson]);

  const selectedTemplate = templates.find(t => t.id === templateId);

  const baseSubject = contentMode === 'CUSTOM'
    ? customEmailSubject
    : contentMode === 'TEMPLATE_EDITED'
      ? customEmailSubject || selectedTemplate?.email_subject
      : selectedTemplate?.email_subject;

  const baseHtml = contentMode === 'CUSTOM'
    ? customEmailBody
    : contentMode === 'TEMPLATE_EDITED'
      ? customEmailBody || selectedTemplate?.email_body_html || selectedTemplate?.email_body_text
      : selectedTemplate?.email_body_html || selectedTemplate?.email_body_text;

  const baseText = contentMode === 'CUSTOM'
    ? customEmailBody
    : contentMode === 'TEMPLATE_EDITED'
      ? customEmailBody || selectedTemplate?.email_body_text
      : selectedTemplate?.email_body_text;

  const renderedSubject = parsedVariables ? renderTemplate(baseSubject || '', parsedVariables) : '';
  const renderedHtml = parsedVariables ? renderTemplate(baseHtml || '', parsedVariables) : '';
  const renderedText = parsedVariables ? renderTemplate(baseText || '', parsedVariables) : '';

  const handleSendTest = async () => {
    setFeedback(null);
    if (!testEmail) {
      setFeedback({ type: 'error', message: 'Veuillez saisir un email de test.' });
      return;
    }
    if (!parsedVariables) {
      setFeedback({ type: 'error', message: 'JSON des variables invalide.' });
      return;
    }
    if (contentMode === 'TEMPLATE' && !templateId) {
      setFeedback({ type: 'error', message: 'Veuillez sélectionner un template.' });
      return;
    }

    setLoading(true);
    try {
      await sendTestEmail({
        toEmail: testEmail,
        relanceType,
        contentMode,
        templateId: templateId || undefined,
        customEmailSubject: customEmailSubject || undefined,
        customEmailBody: customEmailBody || undefined,
        templateVariables: parsedVariables
      });
      setFeedback({ type: 'success', message: 'Email de test envoyé ✅' });
    } catch (err) {
      setFeedback({ type: 'error', message: err?.message || 'Erreur envoi test' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-xl">🛡️</div>
          <div>
            <div className="text-sm font-semibold text-amber-900">Mode TEST sécurisé</div>
            <div className="text-sm text-amber-800 mt-1">
              Aucun impact sur <strong>campagnes</strong>, <strong>queue</strong>, <strong>stats</strong> ou <strong>agenda</strong>.
            </div>
            <div className="text-xs text-amber-700 mt-2">
              Cet envoi ne crée pas de campagne ni de destinataire, et ne déclenche pas n8n.
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-xl p-4 bg-white">
          <div className="text-sm font-medium text-slate-900 mb-2">Sélection campagne (optionnel)</div>
          <select
            value={selectedCampaignId}
            onChange={(e) => handleSelectCampaign(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
          >
            <option value="">Brouillon courant (manuel)</option>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div className="text-xs text-slate-500 mt-2">
            Si vous sélectionnez une campagne, son contenu est chargé uniquement pour le test.
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl p-4 bg-white">
          <div className="text-sm font-medium text-slate-900 mb-2">Email de test</div>
          <input
            type="email"
            placeholder="Email de test"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
          />
          <div className="text-xs text-slate-500 mt-2">
            Envoi direct SMTP uniquement. Pas de queue, pas de campagne.
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-4">
        <div className="text-sm font-medium text-slate-900">Contenu</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select
            value={relanceType}
            onChange={(e) => setRelanceType(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
          >
            <option value="PAYMENT_REMINDER">Rappel paiement</option>
            <option value="OVERDUE_INVOICE">Facture impayée</option>
            <option value="PRESTATION_REMINDER">Rappel prestation</option>
            <option value="ORDER_FOLLOWUP">Suivi commande</option>
            <option value="CUSTOM">Personnalisé</option>
          </select>
          <select
            value={contentMode}
            onChange={(e) => setContentMode(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
          >
            <option value="TEMPLATE">Template</option>
            <option value="TEMPLATE_EDITED">Template modifié</option>
            <option value="CUSTOM">Personnalisé</option>
          </select>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
          >
            <option value="">Template...</option>
            {templates
              .filter(t => t.relance_type === relanceType || relanceType === 'CUSTOM')
              .map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
          </select>
        </div>

        {(contentMode === 'CUSTOM' || contentMode === 'TEMPLATE_EDITED') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Sujet"
              value={customEmailSubject}
              onChange={(e) => setCustomEmailSubject(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
            />
            <textarea
              rows="5"
              placeholder="Corps (HTML ou texte)"
              value={customEmailBody}
              onChange={(e) => setCustomEmailBody(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
            />
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-slate-900">Variables</div>
          <button
            type="button"
            onClick={useSampleVariables}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs"
          >
            Utiliser exemples
          </button>
        </div>
        <textarea
          rows="6"
          value={variablesJson}
          onChange={(e) => setVariablesJson(e.target.value)}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-xs font-mono"
        />
        {!parsedVariables && (
          <div className="text-xs text-red-600">JSON invalide</div>
        )}
      </div>

      <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-4">
        <div className="text-sm font-medium text-slate-900">Preview</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1">HTML rendu</div>
            <div className="border border-slate-100 rounded p-3 bg-slate-50" dangerouslySetInnerHTML={{ __html: renderedHtml || '<em>Aucun HTML</em>' }} />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1">Texte brut</div>
            <div className="text-sm text-slate-900 whitespace-pre-wrap bg-slate-50 rounded p-3">
              {renderedText || '-'}
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs font-medium text-slate-500 mb-1">Sujet</div>
            <div className="text-sm text-slate-900 bg-slate-50 rounded p-3">
              {renderedSubject || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSendTest}
          disabled={loading}
          className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-950 disabled:opacity-50"
        >
          Envoyer un test
        </button>
        {feedback && (
          <div className={`text-sm ${feedback.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
            {feedback.message}
          </div>
        )}
      </div>
    </div>
  );
}
